import { AddSound } from "./components/AddSound";
import { Chat } from "./components/Chat";
import { Listeners } from "./components/Listeners";
import { MicButton } from "./components/MicButton";
import { NowPlaying } from "./components/NowPlaying";
import { SkipButton } from "./components/SkipButton";
import { TrackList } from "./components/TrackList";
import { useAuth } from "./hooks/useAuth";
import { useClock } from "./hooks/useClock";
import { usePlayer, YT_CONTAINER_ID } from "./hooks/usePlayer";
import { useStation } from "./hooks/useStation";
import { signIn, signOutOfRadio } from "./firebase";

export function App() {
  const { user, checking } = useAuth();
  const { serverNow } = useClock();
  const station = useStation(serverNow, Boolean(user));
  const player = usePlayer(station.state, station.current, serverNow);

  if (checking) return <main className="shell shell--center">…</main>;

  if (!user) {
    return (
      <main className="shell shell--center">
        <div className="gate">
          <h1 className="gate__title">Gojob Radio</h1>
          <p className="gate__baseline">
            Ce que les collègues écoutent, maintenant. Mets un son, prends le mic.
          </p>
          <button className="btn btn--primary btn--lg" type="button" onClick={() => void signIn()}>
            Entrer avec Google
          </button>
          <p className="gate__note">Réservé aux comptes @gojob.com</p>
        </div>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="top">
        <div className="brand">
          <span className="brand__dot" aria-hidden />
          Gojob Radio
        </div>
        <Listeners listeners={station.listeners} />
        <button className="btn btn--ghost" type="button" onClick={() => void signOutOfRadio()}>
          {user.displayName ?? user.email}
        </button>
      </header>

      <div className="stage">
        <div className="stage__main">
          <NowPlaying
            track={station.current}
            positionMs={player.positionMs}
            live={player.on}
            myUid={user.uid}
            listeners={station.listeners}
          />

          {!player.on ? (
            <button className="btn btn--primary btn--lg btn--wide" type="button" onClick={player.turnOn}>
              ▶ Écouter
            </button>
          ) : (
            <div className="controls">
              <button className="btn btn--ghost" type="button" onClick={player.turnOff}>
                couper le son
              </button>
              <SkipButton
                disabled={!station.current}
                skipVotes={station.state?.skipVotes}
                listeners={station.listeners}
                myUid={user.uid}
              />
              <MicButton onDuck={player.setDucked} />
            </div>
          )}

          {player.status === "blocked" && (
            <p className="warn">Le navigateur a bloqué la lecture — reclique sur Écouter.</p>
          )}
          {player.error && <p className="warn">{player.error}</p>}

          <AddSound />
        </div>

        <Chat messages={station.messages} myUid={user.uid} />
      </div>

      {/* Hôte du player YouTube : présent en permanence dans le DOM (il ne peut pas
          être créé à la volée), mais hors de vue — on ne veut que le son. */}
      <div className="yt-host" aria-hidden>
        <div id={YT_CONTAINER_ID} />
      </div>

      <div className="columns">
        <section className="panel">
          <h2 className="panel__title">À suivre</h2>
          <TrackList
            tracks={station.queue}
            // Une file vide n'est plus un silence : la radio repique dans
            // l'historique — sauf s'il n'y a pas encore d'historique.
            empty={
              station.history.length > 0
                ? "Rien dans la file — la radio repique dans ce qui est déjà passé. Colle un son, il passe juste après."
                : "Rien dans la file."
            }
            showTime
            myUid={user.uid}
            removable
          />
        </section>
        <section className="panel">
          <h2 className="panel__title">Déjà passé</h2>
          <TrackList tracks={station.history} empty="L'historique est vide." />
        </section>
      </div>
    </main>
  );
}
